import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Search, 
  Eye, 
  Calendar, 
  User, 
  MessageSquare,
  Filter,
  ChevronLeft,
  ChevronRight,
  Loader2,
  AlertTriangle,
  RefreshCw,
  Star,
  ThumbsUp,
  ThumbsDown,
  Package,
  Building2,
  FileText,
  Smile,
  Meh,
  Frown,
  Edit,
  Save,
  Trash2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { useAuth } from "../App";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Satisfaction colors
const SATISFACTION_COLORS = {
  "Excellent": "bg-emerald-100 text-emerald-700 border-emerald-200",
  "Good": "bg-blue-100 text-blue-700 border-blue-200",
  "Average": "bg-amber-100 text-amber-700 border-amber-200",
  "Poor": "bg-red-100 text-red-700 border-red-200",
  "Very Poor": "bg-red-200 text-red-800 border-red-300",
};

const SATISFACTION_ICONS = {
  "Excellent": Smile,
  "Good": Smile,
  "Average": Meh,
  "Poor": Frown,
  "Very Poor": Frown,
};

export default function SampleFeedbackReportPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role?.toLowerCase() === "admin";
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [satisfactionFilter, setSatisfactionFilter] = useState("all");
  const [productTypeFilter, setProductTypeFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingFeedback, setDeletingFeedback] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const itemsPerPage = 15;

  // Satisfaction and effectiveness options
  const satisfactionOptions = ["Excellent", "Good", "Average", "Poor", "Very Poor"];
  const effectivenessOptions = ["Very Effective", "Effective", "Moderately Effective", "Slightly Effective", "Not Effective"];
  const recommendOptions = ["Yes", "No", "Maybe"];

  useEffect(() => {
    fetchFeedbacks();
  }, []);

  const fetchFeedbacks = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/sample-feedback`);
      if (response.data.success) {
        setFeedbacks(response.data.feedbacks || []);
      } else {
        toast.error("Failed to load feedback data");
      }
    } catch (error) {
      toast.error("Failed to load sample feedback data");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const response = await axios.post(`${API_URL}/api/sample-feedback/refresh`);
      if (response.data.success) {
        setFeedbacks(response.data.feedbacks || []);
        toast.success("Data refreshed successfully");
      }
    } catch (error) {
      toast.error("Failed to refresh data");
    } finally {
      setRefreshing(false);
    }
  };

  const openDetailDialog = (feedback) => {
    setSelectedFeedback(feedback);
    setDetailDialogOpen(true);
  };

  const openEditDialog = (feedback) => {
    setSelectedFeedback(feedback);
    setEditForm({
      clientName: feedback.clientName || "",
      companyName: feedback.companyName || "",
      productType: feedback.productType || "",
      productCategory: feedback.productCategory || "",
      overallSatisfaction: feedback.overallSatisfaction || "",
      productEffectiveness: feedback.productEffectiveness || "",
      wouldRecommend: feedback.wouldRecommend || "",
      texture: feedback.texture || "",
      fragrance: feedback.fragrance || "",
      appearance: feedback.appearance || "",
      color: feedback.color || "",
      spreadability: feedback.spreadability || "",
      absorption: feedback.absorption || "",
      afterFeel: feedback.afterFeel || "",
      comments: feedback.comments || "",
      improvements: feedback.improvements || "",
      additionalComments: feedback.additionalComments || "",
    });
    setEditDialogOpen(true);
  };

  const handleEditFormChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveEdit = async () => {
    if (!selectedFeedback?.zohoId) {
      toast.error("Cannot update: Missing Zoho ID");
      return;
    }

    setSaving(true);
    try {
      const response = await axios.put(
        `${API_URL}/api/sample-feedback/${selectedFeedback.zohoId}`,
        editForm
      );

      if (response.data.success) {
        toast.success("Feedback updated successfully!", {
          description: "Changes have been synced to Zoho"
        });
        setEditDialogOpen(false);
        // Refresh the data
        await fetchFeedbacks();
      } else {
        toast.error(response.data.error || "Failed to update feedback");
      }
    } catch (error) {
      console.error("Error updating feedback:", error);
      toast.error("Failed to update feedback", {
        description: error.response?.data?.error || error.message
      });
    } finally {
      setSaving(false);
    }
  };

  // Delete feedback (Admin only)
  const handleDelete = async () => {
    if (!deletingFeedback?.zohoId) {
      toast.error("Cannot delete: Missing Zoho ID");
      return;
    }

    setDeleting(true);
    try {
      const response = await axios.delete(
        `${API_URL}/api/sample-feedback/${deletingFeedback.zohoId}`,
        { params: { user_role: user?.role } }
      );

      if (response.data.success) {
        toast.success("Feedback record deleted successfully!");
        setDeleteDialogOpen(false);
        setDeletingFeedback(null);
        await fetchFeedbacks();
      } else {
        toast.error(response.data.error || "Failed to delete feedback");
      }
    } catch (error) {
      console.error("Error deleting feedback:", error);
      toast.error(error.response?.data?.detail || "Failed to delete feedback");
    } finally {
      setDeleting(false);
    }
  };

  // Get unique product types for filter
  const productTypes = [...new Set(feedbacks.map(f => f.productType).filter(Boolean))];
  const satisfactionLevels = [...new Set(feedbacks.map(f => f.overallSatisfaction).filter(Boolean))];

  // Filter feedbacks
  const filteredFeedbacks = feedbacks.filter(feedback => {
    const matchesSearch = 
      (feedback.testNo?.toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
      (feedback.clientName?.toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
      (feedback.companyName?.toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
      (feedback.productName?.toLowerCase() || "").includes(searchTerm.toLowerCase());
    
    const matchesSatisfaction = satisfactionFilter === "all" || feedback.overallSatisfaction === satisfactionFilter;
    const matchesType = productTypeFilter === "all" || feedback.productType === productTypeFilter;
    
    return matchesSearch && matchesSatisfaction && matchesType;
  });

  // Pagination
  const totalPages = Math.ceil(filteredFeedbacks.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedFeedbacks = filteredFeedbacks.slice(startIndex, startIndex + itemsPerPage);

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // Get satisfaction badge
  const getSatisfactionBadge = (satisfaction) => {
    if (!satisfaction) return <Badge variant="outline" className="font-poppins">-</Badge>;
    const colorClass = SATISFACTION_COLORS[satisfaction] || "bg-gray-100 text-gray-700 border-gray-200";
    const SatIcon = SATISFACTION_ICONS[satisfaction] || Meh;
    return (
      <Badge className={`${colorClass} border font-poppins font-medium px-2.5 py-1 flex items-center gap-1.5`}>
        <SatIcon className="w-3.5 h-3.5" />
        {satisfaction}
      </Badge>
    );
  };

  // Get recommend badge
  const getRecommendBadge = (recommend) => {
    if (!recommend) return "-";
    const isYes = recommend.toLowerCase().includes("yes");
    return (
      <Badge className={`${isYes ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'} border font-poppins`}>
        {isYes ? <ThumbsUp className="w-3 h-3 mr-1" /> : <ThumbsDown className="w-3 h-3 mr-1" />}
        {recommend}
      </Badge>
    );
  };

  // Calculate stats
  const totalFeedbacks = feedbacks.length;
  const excellentCount = feedbacks.filter(f => f.overallSatisfaction === "Excellent").length;
  const goodCount = feedbacks.filter(f => f.overallSatisfaction === "Good").length;
  const wouldRecommendCount = feedbacks.filter(f => f.wouldRecommend?.toLowerCase().includes("yes")).length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-vcc-cream-50 to-white">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-vcc-teal animate-spin mx-auto mb-4" />
          <p className="text-slate-600 font-poppins">Loading sample feedback report...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-vcc-cream-50 via-white to-vcc-pink-50 p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-poppins font-bold text-slate-800 flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-vcc-coral to-vcc-coral/70 rounded-xl flex items-center justify-center shadow-lg">
                <MessageSquare className="w-6 h-6 text-white" />
              </div>
              Sample Feedback Report
            </h1>
            <p className="text-slate-500 font-poppins mt-2 ml-15">
              View and analyze client feedback on product samples
            </p>
          </div>
          <Button
            onClick={handleRefresh}
            variant="outline"
            className="font-poppins border-vcc-teal/30 text-vcc-teal hover:bg-vcc-teal/10"
            disabled={refreshing}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh from Zoho
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl p-5 border border-slate-100 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800 font-poppins">{totalFeedbacks}</p>
              <p className="text-sm text-slate-500 font-poppins">Total Feedbacks</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-5 border border-slate-100 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <Smile className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800 font-poppins">{excellentCount}</p>
              <p className="text-sm text-slate-500 font-poppins">Excellent Rating</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-5 border border-slate-100 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Star className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800 font-poppins">{goodCount}</p>
              <p className="text-sm text-slate-500 font-poppins">Good Rating</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-5 border border-slate-100 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <ThumbsUp className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800 font-poppins">{wouldRecommendCount}</p>
              <p className="text-sm text-slate-500 font-poppins">Would Recommend</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 mb-6 border border-slate-100 shadow-sm">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[250px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Search by sample no, client, company, or product..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="pl-10 font-poppins"
                data-testid="search-input"
              />
            </div>
          </div>
          
          <Select value={satisfactionFilter} onValueChange={(v) => { setSatisfactionFilter(v); setCurrentPage(1); }}>
            <SelectTrigger className="w-[180px] font-poppins" data-testid="satisfaction-filter">
              <SelectValue placeholder="All Satisfaction" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Satisfaction</SelectItem>
              {satisfactionLevels.map(level => (
                <SelectItem key={level} value={level}>{level}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={productTypeFilter} onValueChange={(v) => { setProductTypeFilter(v); setCurrentPage(1); }}>
            <SelectTrigger className="w-[200px] font-poppins" data-testid="type-filter">
              <SelectValue placeholder="All Product Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Product Types</SelectItem>
              {productTypes.map(type => (
                <SelectItem key={type} value={type}>{type}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Results count */}
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-slate-500 font-poppins">
          Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredFeedbacks.length)} of {filteredFeedbacks.length} feedbacks
        </p>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/80">
              <TableHead className="font-poppins font-semibold text-slate-700">Test No</TableHead>
              <TableHead className="font-poppins font-semibold text-slate-700">Client Name</TableHead>
              <TableHead className="font-poppins font-semibold text-slate-700">Company</TableHead>
              <TableHead className="font-poppins font-semibold text-slate-700">Product Type</TableHead>
              <TableHead className="font-poppins font-semibold text-slate-700">Category</TableHead>
              <TableHead className="font-poppins font-semibold text-slate-700">Satisfaction</TableHead>
              <TableHead className="font-poppins font-semibold text-slate-700">Effectiveness</TableHead>
              <TableHead className="font-poppins font-semibold text-slate-700">Recommend</TableHead>
              <TableHead className="font-poppins font-semibold text-slate-700 text-center">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedFeedbacks.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-12">
                  <div className="flex flex-col items-center gap-3">
                    <AlertTriangle className="w-10 h-10 text-slate-300" />
                    <p className="text-slate-500 font-poppins">No feedback records found</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              paginatedFeedbacks.map((feedback) => (
                <TableRow 
                  key={feedback.id} 
                  className="hover:bg-slate-50/50 transition-colors cursor-pointer"
                  onClick={() => openDetailDialog(feedback)}
                  data-testid={`feedback-row-${feedback.testNo}`}
                >
                  <TableCell className="font-poppins font-medium text-vcc-teal">
                    {feedback.testNo || "-"}
                  </TableCell>
                  <TableCell className="font-poppins text-slate-800">
                    <div className="flex items-center gap-2">
                      <User className="w-3.5 h-3.5 text-slate-400" />
                      {feedback.clientName || "-"}
                    </div>
                  </TableCell>
                  <TableCell className="font-poppins text-slate-600">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-3.5 h-3.5 text-slate-400" />
                      {feedback.companyName || "-"}
                    </div>
                  </TableCell>
                  <TableCell className="font-poppins text-slate-600">
                    <Badge variant="outline" className="font-poppins bg-slate-50">
                      {feedback.productType || "-"}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-poppins text-slate-600">
                    {feedback.productCategory || "-"}
                  </TableCell>
                  <TableCell>
                    {getSatisfactionBadge(feedback.overallSatisfaction)}
                  </TableCell>
                  <TableCell className="font-poppins text-slate-600">
                    {feedback.productEffectiveness || "-"}
                  </TableCell>
                  <TableCell>
                    {getRecommendBadge(feedback.wouldRecommend)}
                  </TableCell>
                  <TableCell className="text-center">
                    <div className="flex items-center justify-center gap-2">
                      <Button
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          openDetailDialog(feedback);
                        }}
                        style={{ backgroundColor: '#2563eb', color: 'white' }}
                        className="hover:opacity-90 font-poppins shadow-sm"
                        data-testid={`view-feedback-${feedback.testNo}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditDialog(feedback);
                        }}
                        className="font-poppins border-amber-300 text-amber-600 hover:bg-amber-50"
                        data-testid={`edit-feedback-${feedback.testNo}`}
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                      {isAdmin && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeletingFeedback(feedback);
                            setDeleteDialogOpen(true);
                          }}
                          className="font-poppins border-red-300 text-red-600 hover:bg-red-50"
                          data-testid={`delete-feedback-${feedback.testNo}`}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Delete
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="font-poppins"
          >
            <ChevronLeft className="w-4 h-4" />
            Previous
          </Button>
          
          <div className="flex items-center gap-1">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              
              return (
                <Button
                  key={pageNum}
                  variant={currentPage === pageNum ? "default" : "outline"}
                  size="sm"
                  onClick={() => setCurrentPage(pageNum)}
                  className={`font-poppins w-9 ${currentPage === pageNum ? 'bg-vcc-teal' : ''}`}
                >
                  {pageNum}
                </Button>
              );
            })}
          </div>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="font-poppins"
          >
            Next
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-poppins text-xl flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-vcc-coral" />
              Feedback Details - {selectedFeedback?.testNo}
            </DialogTitle>
          </DialogHeader>
          
          {selectedFeedback && (
            <div className="space-y-6 mt-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-xs text-slate-500 font-poppins uppercase tracking-wider">Test/Sample No</p>
                  <p className="text-lg font-semibold text-vcc-teal font-poppins mt-1">{selectedFeedback.testNo || "-"}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-xs text-slate-500 font-poppins uppercase tracking-wider">Client Name</p>
                  <p className="text-lg font-semibold text-slate-800 font-poppins mt-1">{selectedFeedback.clientName || "-"}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-xs text-slate-500 font-poppins uppercase tracking-wider">Company</p>
                  <p className="text-sm font-medium text-slate-700 font-poppins mt-1">{selectedFeedback.companyName || "-"}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-xs text-slate-500 font-poppins uppercase tracking-wider">Product Type</p>
                  <p className="text-sm font-medium text-slate-700 font-poppins mt-1">{selectedFeedback.productType || "-"}</p>
                </div>
              </div>

              {/* Product Category */}
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-xs text-slate-500 font-poppins uppercase tracking-wider">Product Category</p>
                <p className="text-sm font-medium text-slate-700 font-poppins mt-1">{selectedFeedback.productCategory || "-"}</p>
              </div>

              {/* Ratings Section */}
              <div className="border-t pt-4">
                <h3 className="text-sm font-semibold text-slate-700 font-poppins mb-3">Ratings & Feedback</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="bg-white border rounded-lg p-3">
                    <p className="text-xs text-slate-500 font-poppins">Overall Satisfaction</p>
                    <div className="mt-2">{getSatisfactionBadge(selectedFeedback.overallSatisfaction)}</div>
                  </div>
                  <div className="bg-white border rounded-lg p-3">
                    <p className="text-xs text-slate-500 font-poppins">Product Effectiveness</p>
                    <p className="text-sm font-medium text-slate-700 font-poppins mt-1">{selectedFeedback.productEffectiveness || "-"}</p>
                  </div>
                  <div className="bg-white border rounded-lg p-3">
                    <p className="text-xs text-slate-500 font-poppins">Would Recommend</p>
                    <div className="mt-2">{getRecommendBadge(selectedFeedback.wouldRecommend)}</div>
                  </div>
                  {selectedFeedback.texture && (
                    <div className="bg-white border rounded-lg p-3">
                      <p className="text-xs text-slate-500 font-poppins">Texture</p>
                      <p className="text-sm font-medium text-slate-700 font-poppins mt-1">{selectedFeedback.texture}</p>
                    </div>
                  )}
                  {selectedFeedback.fragrance && (
                    <div className="bg-white border rounded-lg p-3">
                      <p className="text-xs text-slate-500 font-poppins">Fragrance</p>
                      <p className="text-sm font-medium text-slate-700 font-poppins mt-1">{selectedFeedback.fragrance}</p>
                    </div>
                  )}
                  {selectedFeedback.appearance && (
                    <div className="bg-white border rounded-lg p-3">
                      <p className="text-xs text-slate-500 font-poppins">Appearance</p>
                      <p className="text-sm font-medium text-slate-700 font-poppins mt-1">{selectedFeedback.appearance}</p>
                    </div>
                  )}
                  {selectedFeedback.color && (
                    <div className="bg-white border rounded-lg p-3">
                      <p className="text-xs text-slate-500 font-poppins">Color</p>
                      <p className="text-sm font-medium text-slate-700 font-poppins mt-1">{selectedFeedback.color}</p>
                    </div>
                  )}
                  {selectedFeedback.spreadability && (
                    <div className="bg-white border rounded-lg p-3">
                      <p className="text-xs text-slate-500 font-poppins">Spreadability</p>
                      <p className="text-sm font-medium text-slate-700 font-poppins mt-1">{selectedFeedback.spreadability}</p>
                    </div>
                  )}
                  {selectedFeedback.absorption && (
                    <div className="bg-white border rounded-lg p-3">
                      <p className="text-xs text-slate-500 font-poppins">Absorption</p>
                      <p className="text-sm font-medium text-slate-700 font-poppins mt-1">{selectedFeedback.absorption}</p>
                    </div>
                  )}
                  {selectedFeedback.afterFeel && (
                    <div className="bg-white border rounded-lg p-3">
                      <p className="text-xs text-slate-500 font-poppins">After Feel</p>
                      <p className="text-sm font-medium text-slate-700 font-poppins mt-1">{selectedFeedback.afterFeel}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Comments Section */}
              {(selectedFeedback.comments || selectedFeedback.improvements || selectedFeedback.additionalComments) && (
                <div className="border-t pt-4">
                  <h3 className="text-sm font-semibold text-slate-700 font-poppins mb-3">Comments & Suggestions</h3>
                  {selectedFeedback.comments && (
                    <div className="bg-amber-50 border border-amber-100 rounded-lg p-4 mb-3">
                      <p className="text-xs text-amber-600 font-poppins uppercase tracking-wider mb-1">Comments</p>
                      <p className="text-sm text-slate-700 font-poppins">{selectedFeedback.comments}</p>
                    </div>
                  )}
                  {selectedFeedback.improvements && (
                    <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-3">
                      <p className="text-xs text-blue-600 font-poppins uppercase tracking-wider mb-1">Suggested Improvements</p>
                      <p className="text-sm text-slate-700 font-poppins">{selectedFeedback.improvements}</p>
                    </div>
                  )}
                  {selectedFeedback.additionalComments && (
                    <div className="bg-slate-50 border border-slate-100 rounded-lg p-4">
                      <p className="text-xs text-slate-500 font-poppins uppercase tracking-wider mb-1">Additional Comments</p>
                      <p className="text-sm text-slate-700 font-poppins">{selectedFeedback.additionalComments}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Metadata */}
              <div className="border-t pt-4 text-xs text-slate-400 font-poppins">
                <p>Zoho ID: {selectedFeedback.zohoId}</p>
                {selectedFeedback.createdTime && <p>Created: {formatDate(selectedFeedback.createdTime)}</p>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-poppins text-xl flex items-center gap-2">
              <Edit className="w-5 h-5 text-amber-500" />
              Edit Feedback - {selectedFeedback?.testNo}
            </DialogTitle>
          </DialogHeader>
          
          {selectedFeedback && (
            <div className="space-y-6 mt-4">
              {/* Basic Info (Read-only) */}
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-xs text-slate-500 font-poppins uppercase tracking-wider mb-2">Sample Information (Read-only)</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-slate-400 font-poppins">Test/Sample No</p>
                    <p className="text-sm font-semibold text-vcc-teal font-poppins">{selectedFeedback.testNo}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400 font-poppins">Zoho ID</p>
                    <p className="text-sm font-medium text-slate-600 font-poppins">{selectedFeedback.zohoId}</p>
                  </div>
                </div>
              </div>

              {/* Editable Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="font-poppins text-sm">Client Name</Label>
                  <Input
                    value={editForm.clientName}
                    onChange={(e) => handleEditFormChange("clientName", e.target.value)}
                    className="font-poppins"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-poppins text-sm">Company Name</Label>
                  <Input
                    value={editForm.companyName}
                    onChange={(e) => handleEditFormChange("companyName", e.target.value)}
                    className="font-poppins"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-poppins text-sm">Product Type</Label>
                  <Input
                    value={editForm.productType}
                    onChange={(e) => handleEditFormChange("productType", e.target.value)}
                    className="font-poppins"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-poppins text-sm">Product Category</Label>
                  <Input
                    value={editForm.productCategory}
                    onChange={(e) => handleEditFormChange("productCategory", e.target.value)}
                    className="font-poppins"
                  />
                </div>
              </div>

              {/* Ratings Section */}
              <div className="border-t pt-4">
                <h3 className="text-sm font-semibold text-slate-700 font-poppins mb-3">Ratings & Feedback</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="font-poppins text-sm">Overall Satisfaction</Label>
                    <Select value={editForm.overallSatisfaction} onValueChange={(v) => handleEditFormChange("overallSatisfaction", v)}>
                      <SelectTrigger className="font-poppins">
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        {satisfactionOptions.map(opt => (
                          <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="font-poppins text-sm">Product Effectiveness</Label>
                    <Select value={editForm.productEffectiveness} onValueChange={(v) => handleEditFormChange("productEffectiveness", v)}>
                      <SelectTrigger className="font-poppins">
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        {effectivenessOptions.map(opt => (
                          <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="font-poppins text-sm">Would Recommend</Label>
                    <Select value={editForm.wouldRecommend} onValueChange={(v) => handleEditFormChange("wouldRecommend", v)}>
                      <SelectTrigger className="font-poppins">
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        {recommendOptions.map(opt => (
                          <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Product Attributes */}
              <div className="border-t pt-4">
                <h3 className="text-sm font-semibold text-slate-700 font-poppins mb-3">Product Attributes</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="font-poppins text-sm">Texture</Label>
                    <Input
                      value={editForm.texture}
                      onChange={(e) => handleEditFormChange("texture", e.target.value)}
                      className="font-poppins"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-poppins text-sm">Fragrance</Label>
                    <Input
                      value={editForm.fragrance}
                      onChange={(e) => handleEditFormChange("fragrance", e.target.value)}
                      className="font-poppins"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-poppins text-sm">Appearance</Label>
                    <Input
                      value={editForm.appearance}
                      onChange={(e) => handleEditFormChange("appearance", e.target.value)}
                      className="font-poppins"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-poppins text-sm">Color</Label>
                    <Input
                      value={editForm.color}
                      onChange={(e) => handleEditFormChange("color", e.target.value)}
                      className="font-poppins"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-poppins text-sm">Spreadability</Label>
                    <Input
                      value={editForm.spreadability}
                      onChange={(e) => handleEditFormChange("spreadability", e.target.value)}
                      className="font-poppins"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-poppins text-sm">Absorption</Label>
                    <Input
                      value={editForm.absorption}
                      onChange={(e) => handleEditFormChange("absorption", e.target.value)}
                      className="font-poppins"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-poppins text-sm">After Feel</Label>
                    <Input
                      value={editForm.afterFeel}
                      onChange={(e) => handleEditFormChange("afterFeel", e.target.value)}
                      className="font-poppins"
                    />
                  </div>
                </div>
              </div>

              {/* Comments Section */}
              <div className="border-t pt-4">
                <h3 className="text-sm font-semibold text-slate-700 font-poppins mb-3">Comments & Suggestions</h3>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="font-poppins text-sm">Comments</Label>
                    <Textarea
                      value={editForm.comments}
                      onChange={(e) => handleEditFormChange("comments", e.target.value)}
                      className="font-poppins min-h-[80px]"
                      placeholder="Enter comments..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-poppins text-sm">Suggested Improvements</Label>
                    <Textarea
                      value={editForm.improvements}
                      onChange={(e) => handleEditFormChange("improvements", e.target.value)}
                      className="font-poppins min-h-[80px]"
                      placeholder="Enter improvement suggestions..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-poppins text-sm">Additional Comments</Label>
                    <Textarea
                      value={editForm.additionalComments}
                      onChange={(e) => handleEditFormChange("additionalComments", e.target.value)}
                      className="font-poppins min-h-[80px]"
                      placeholder="Enter additional comments..."
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="mt-6">
            <Button
              variant="outline"
              onClick={() => setEditDialogOpen(false)}
              disabled={saving}
              className="font-poppins"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveEdit}
              disabled={saving}
              style={{ backgroundColor: '#2563eb', color: 'white' }}
              className="hover:opacity-90 font-poppins"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save & Sync to Zoho
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog (Admin Only) */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-poppins text-red-600">Delete Feedback Record</AlertDialogTitle>
            <AlertDialogDescription className="font-poppins">
              Are you sure you want to delete the feedback record for <strong>{deletingFeedback?.testNo}</strong>?
              <br /><br />
              This will permanently remove the record from Zoho Creator. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="font-poppins" disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 font-poppins"
            >
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
